import React from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Grid,
  Button,
  Divider,
  Chip,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import downloadValidationErrorFile from "src/utils/downloadValidationErrors";

export default function ImportSummaryModal({
  importSummaryReport,
  open,
  onClose,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "6px",
          overflow: "hidden",
          height: "90vh",
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Fixed Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #f0f0f0",
            background: "#fff",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Typography
            variant="inherit"
            sx={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#1f2937",
            }}
          >
            Import Summary Report
          </Typography>

          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 4,
            py: 2,
          }}
        >
          {/* Title */}
          <Typography
            variant="inherit"
            align="center"
            sx={{
              fontSize: "32px",
              fontWeight: 700,
              color: "#111827",
              mb: 1,
            }}
          >
            Import Completed with Errors
          </Typography>

          <Typography
            variant="inherit"
            align="center"
            sx={{
              color: "#6b7280",
              fontSize: "15px",
              mb: 4,
            }}
          >
            {importSummaryReport?.successCount} of {importSummaryReport?.totalCount} records were imported. Please review the failures below.
          </Typography>

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 4, mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  p: 3,
                  background: "#ffffff",
                }}
              >
                <Typography
                  variant="inherit"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#10b981",
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    mb: 1,
                  }}
                >
                  Successful
                </Typography>

                <Typography
                  variant="inherit"
                  sx={{
                    fontSize: "48px",
                    fontWeight: 700,
                    color: "#111827",
                  }}
                >
                  {importSummaryReport?.successCount}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  border: "1px solid #fee2e2",
                  borderRadius: "12px",
                  p: 3,
                  background: "#fff5f5",
                }}
              >
                <Typography
                  variant="inherit"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#ef4444",
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    mb: 1,
                  }}
                >
                  Failed
                </Typography>

                <Typography
                  variant="inherit"
                  sx={{
                    fontSize: "48px",
                    fontWeight: 700,
                    color: "#111827",
                  }}
                >
                  {importSummaryReport?.failedCount}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Error Section */}
          <Box
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            {/* Error Header */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "#fafafa",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ErrorOutlineIcon
                  sx={{ color: "#ef4444", fontSize: "18px" }}
                />

                <Typography
                  variant="inherit"
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#111827",
                  }}
                >
                  Validation Error Details
                </Typography>
              </Box>

              <Chip
                label={importSummaryReport?.failedCount + " Issues"}
                size="small"
                sx={{
                  background: "#f3f4f6",
                  color: "#6b7280",
                  fontWeight: 600,
                }}
              />
            </Box>

            {/* Table Header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "100px 1fr",
                px: 3,
                py: 1.5,
                background: "#ffffff",
                borderTop: "1px solid #f3f4f6",
                borderBottom: "1px solid #f3f4f6",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              <Typography
                variant="inherit"
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#6b7280",
                }}
              >
                Row #
              </Typography>

              <Typography
                variant="inherit"
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#6b7280",
                }}
              >
                Error Reason
              </Typography>
            </Box>

            {/* Error Rows */}
            {importSummaryReport?.errors?.map((item, index) => (
              <Box key={index}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "100px 1fr",
                    px: 3,
                    py: 2,
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="inherit"
                    sx={{
                      color: "#ef4444",
                      fontWeight: 700,
                      fontSize: "14px",
                    }}
                  >
                    {item.rowNumber}
                  </Typography>

                  <Typography
                    variant="inherit"
                    sx={{
                      color: "#ef4444",
                      fontSize: "14px",
                    }}
                  >
                    {item.reason}
                  </Typography>
                </Box>

                {index !== importSummaryReport.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Fixed Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid #f0f0f0",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button
            variant="contained"
            startIcon={<DownloadOutlinedIcon />}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              background: "#eef2ff",
              color: "#4f46e5",
              boxShadow: "none",
              "&:hover": {
                background: "#e0e7ff",
                boxShadow: "none",
              },
            }}
            onClick={() => downloadValidationErrorFile(importSummaryReport?.errors)}
          >
            Download Error File (.xlsx)
          </Button>

          <Button
            onClick={onClose}
            sx={{
              textTransform: "none",
              color: "#6b7280",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}